import { createFileRoute, useParams, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/src/components/AuthContextProvider'
import { useTranslation } from '@/src/i18n/client' 
import { UpsertWorkflowForm } from '@/src/components/manage/mutate/upsertWorkflow'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { getWorkflowById, upsertWorkflow } from '@/src/api/workflow'
import { StatusSwitch } from '@/src/components/ui/status-switch'
import { GenericAction } from '@/src/components/manage/actions/GenericAction'
import { toast } from 'sonner'
import { PERMISSIONS } from '@/permission.config'
import { useState } from 'react'
import { UploadIcon } from '@phosphor-icons/react'

export const Route = createFileRoute('/_manage/manage/workflows/$id')({
  component: WorkflowEditPage,
})

function WorkflowEditPage() {
  const { id } = useParams({ from: '/_manage/manage/workflows/$id' })
  const { t } = useTranslation('manage');
  const isNew = id === 'new'
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { permissions, isAdmin } = useAuth()
  const [importDialogRequestId, setImportDialogRequestId] = useState(0)
  
  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.WORKFLOWS_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])

  const { data: workflow, isLoading, error } = useQuery({
    queryKey: ['workflow', 'get', id],
    queryFn: () => getWorkflowById({ data: { id } }),
    enabled: !isNew,
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!workflow) return
      await upsertWorkflow({
        data: {
          ...(workflow as any),
          enabled,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', 'get', id] })
      queryClient.invalidateQueries({ queryKey: ['workflow', 'list'] })
      toast.success(t('workflows.statusUpdated'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('failedToUpdateStatus'))
    },
  })

  const handleEnabledChange = (enabled: boolean) => {
    if (isNew) return
    toggleEnabledMutation.mutate(enabled)
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            {isNew ? t('workflows.createNewTitle') : t('workflows.editTitle')}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          {!isNew && (
            <StatusSwitch
              id="workflow-enabled"
              label={t('table.enabled')}
              checked={(workflow as any)?.enabled ?? true}
              onCheckedChange={handleEnabledChange}
              disabled={isLoading || toggleEnabledMutation.isPending}
            />
          )}
          <GenericAction
            label={t('workflows.importButton')}
            icon={UploadIcon}
            variant="secondary"
            size="lg"
            onClick={() => setImportDialogRequestId((v) => v + 1)}
          />
        </AdminToolbar.Right>
      </AdminToolbar>
      <UpsertWorkflowForm
        id={id}
        isNew={isNew}
        workflow={workflow}
        isLoading={isLoading}
        callbackUrl="/manage/workflows"
        importDialogRequestId={importDialogRequestId}
      />
      {error && <div className="text-destructive mt-2">{error instanceof Error ? error.message : t('workflows.failedToLoadWorkflow')}</div>}
    </div>
  )
}
